import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil, delay } from 'rxjs/operators';
import { AmountType } from '../../types/Transaction';
import { createControl } from '../../../create-request/utilities/formUtilities';
import { AchTransactionDetailsForm, CheckTransactionDetailsForm, ItTransactionDetailsForm, WireTransactionDetailsForm } from '../../../create-request/models/form/TransactionFormGroup';
import { DistributionReason } from '../../../create-request/models/internal-transfer/DistributionReason';
import { selectNraIndicator, selectRegType } from '../../../account-info-header/reducers';
import { Constants, distributionReasons, registrationTypes } from '../../utilities/Constants';
import { selectCheckUseStandingAuth, selectIsBundledOneTimeTotalDist, selectTaxWithholdings } from '../../../create-request/reducers';
import { selectIsEnableRecurringChecksStandingAuth } from 'projects/money-movement-ui/src/app/feature-flags/reducers';

@Component({
  standalone: false,
  selector: 'mm-amount-type-selector',
  templateUrl: './amount-type-selector.component.html',
  styleUrls: ['./amount-type-selector.component.scss']
})
export class AmountTypeSelectorComponent implements OnInit {
  @Input() parentForm: FormGroup<CheckTransactionDetailsForm | WireTransactionDetailsForm | ItTransactionDetailsForm | AchTransactionDetailsForm>;
  @Input() prefilledValue: AmountType;
  @Input() controlDisabled: boolean = false;
  @Input() autoCalculateRmd$: Observable<boolean> = of(false);
  @Input() payIncome$: Observable<string | undefined>;
  @Input() distributionReason$: Observable<DistributionReason | null> = of(null);
  @Input() distributionReasonDescription$: Observable<string | null> = of(null); // used for multiple to account flow, IT
  @Input() isFirstPartyIraToIraJournal$: Observable<boolean> = of(false);
  @Input() grossNetRadioEditContainer: boolean = false;
  @Input() isPJLS: boolean = false;
  @Input() PJLSindex: number = 0;
  @Input() achOnRequest: boolean = false;
  @Input() isDetached: boolean = false;
  @Input() frequency$: Observable<"recurring" | "onetime" | "onetimeTotalDist" | undefined> = of('onetime');
  @Input() transactionType: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  disableGross = false;
  disableNet = false;

  constructor(private store: Store) { }

  ngOnInit(): void {
    // TODO: if frequency is lastbizday OR isTaxWithheld is false -> amountType = gross and disabled = true ?
    createControl(this.parentForm, 'amountType', 'gross');

    if (this.prefilledValue) {
      this.amountType?.setValue(this.prefilledValue);
      this.disableGross = this.controlDisabled;
      this.disableNet = this.controlDisabled;
    }

    combineLatest([
      this.distributionReason$,
      this.distributionReasonDescription$,
      this.autoCalculateRmd$.pipe(distinctUntilChanged()),
      this.payIncome$,
      this.isFirstPartyIraToIraJournal$,
      this.store.select(selectRegType).pipe(takeUntil(this.destroy$)),
      this.store.select(selectNraIndicator),
      this.store.select(selectTaxWithholdings).pipe(debounceTime(100)),
      this.frequency$,
      this.store.select(selectIsBundledOneTimeTotalDist),
      this.store.select(selectIsEnableRecurringChecksStandingAuth),
      this.store.select(selectCheckUseStandingAuth)
    ])
      .pipe(takeUntil(this.destroy$),
        delay(0)
      )
      .subscribe(
        ([
          distributionReason,
          distributionReasonDescription,
          autocalculateRMd,
          payIncomeSelected,
          isFirstPartyIraToIraJournal,
          regType,
          isNraAccount,
          taxWithholdings,
          frequency,
          isBundledOneTimeTotalDist,
          enableRecurringChecksStandingAuth,
          useStandingAuth
        ]) => {
          if (
            this.controlDisabled ||
            payIncomeSelected ||
            regType === registrationTypes.educationalSavings ||
            autocalculateRMd ||
            distributionReason?.description === 'QUALIFIED (Q)' ||
            distributionReasonDescription === 'QUALIFIED (Q)' ||
            distributionReason?.description === 'QCD (7-QCD)' ||
            distributionReasonDescription === 'QCD (7-QCD)' ||
             distributionReason?.description === 'QCD (Y4)' ||
            distributionReasonDescription === 'QCD (Y4)' ||
            isNraAccount ||
            isFirstPartyIraToIraJournal && distributionReason?.description === 'COMBINE ACCOUNTS (0-COMB)' ||
            isFirstPartyIraToIraJournal && distributionReasonDescription === 'COMBINE ACCOUNTS (0-COMB)' ||
            (taxWithholdings.fedTaxPercent === Constants.zeroPercent && 
              taxWithholdings.stateTaxPercent === Constants.zeroPercent)
          ) {
            this.amountType?.disable();
            this.amountType?.setValue(this.prefilledValue ?? 'gross');
            this.amountType?.markAsUntouched();
          } else {
            this.amountType?.enable();
          }

          if((frequency === 'onetimeTotalDist' || isBundledOneTimeTotalDist) && this.transactionType === 'check') {
            this.amountType?.disable();
            this.amountType?.setValue(this.prefilledValue ?? 'gross');
            this.amountType?.markAsUntouched();
          }

          if (enableRecurringChecksStandingAuth && frequency === 'recurring' && (useStandingAuth === 'yes' || useStandingAuth === 'bundled-yes') && this.transactionType === 'check') {
            const isIneligibleForWithholding =
              distributionReason?.description === distributionReasons.qcd ||
              distributionReasonDescription === distributionReasons.qcd ||
              distributionReason?.description === distributionReasons.qcdY4 ||
              distributionReasonDescription === distributionReasons.qcdY4 ||
              distributionReason?.description === distributionReasons.qualified ||
              distributionReasonDescription === distributionReasons.qualified ||
              regType === registrationTypes.educationalSavings || autocalculateRMd ||
              (taxWithholdings.fedTaxPercent === Constants.zeroPercent &&
              taxWithholdings.stateTaxPercent === Constants.zeroPercent);

            if (isIneligibleForWithholding) {
              this.amountType?.setValue('gross');
              this.amountType?.disable();
            } else {
              this.amountType?.enable();
             
            }
          }
        }
      );

  }

  ngOnChanges() {
    if (this.prefilledValue) {
      this.amountType?.setValue(this.prefilledValue);

      if (this.controlDisabled) {
        this.disableGross = true;
        this.disableNet = true;
      }
      else {
        this.disableGross = false;
        this.disableNet = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.parentForm?.removeControl('amountType');

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  setName() {
    if (this.isPJLS) {
      return "amountType" + this.PJLSindex.toString();
    }
    else {
      return "amountType";
    }
  }

  get amountType() {
    return this.parentForm.controls.amountType;
  }

  get grossNetClass() {
    if (this.grossNetRadioEditContainer) {
      return 'gross-net-radio-container-edit';
    } else if (this.isPJLS) {
      return 'gross-net-radio-container-pjls';
    } else if (this.isDetached) {
      return 'detached-control' ;
    } else {
      return 'gross-net-radio-container';
    }
  }

}
